import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Send } from "lucide-react";
import { toast } from "sonner";
import { useRef } from "react";
import { PlaceholderBar } from "@/components/EmailPlaceholders";

export default function SendReviewModal({
  open, send, sending, sentLink, emailSent, linkOnly = false, onSend, onClose,
  subject = "", body = "", onSubjectChange, onBodyChange,
  saveDefault = false, onSaveDefaultChange, onResetDefault,
}) {
  const activeRef = useRef(null);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="send-review-modal">
      <Card className="max-w-lg w-full p-6 space-y-4 bg-white max-h-[90vh] overflow-y-auto">
        <h3 className="font-alt font-semibold text-cass-navy">{sentLink && linkOnly ? "Secure proposal link" : "Review email before sending"}</h3>
        {sentLink ? (
          <div className="space-y-2 text-sm">
            <p className="text-emerald-600 font-medium">{linkOnly ? "Link created \u2014 copy it into your email" : (emailSent ? "Email sent \u2713" : "Ready \u2014 email not delivered, share the link manually")}</p>
            <div><Label className="text-xs">Secure proposal link</Label><Input readOnly value={sentLink} data-testid="review-link" /></div>
            <Button variant="outline" onClick={() => { navigator.clipboard.writeText(sentLink); toast.success("Link copied"); }} data-testid="review-copy-link">Copy link</Button>
          </div>
        ) : (
          <div className="space-y-3 text-sm">
            <div><span className="text-cass-slate">To:</span> {send.recipient_name} &lt;{send.recipient_email}&gt;</div>
            <div className="flex items-center justify-between">
              <Label className="text-xs">Subject</Label>
              {onResetDefault && <button type="button" onClick={onResetDefault} className="text-[11px] text-cass-teal underline" data-testid="review-reset-default">Reset to saved default</button>}
            </div>
            <Input value={subject} onChange={(e) => onSubjectChange && onSubjectChange(e.target.value)} onFocus={(e) => { activeRef.current = { el: e.target, apply: (v) => onSubjectChange && onSubjectChange(v) }; }} data-testid="review-subject" />
            <div>
              <Label className="text-xs">Email message (intro that wraps the secure link)</Label>
              <Textarea rows={7} value={body} onChange={(e) => onBodyChange && onBodyChange(e.target.value)} onFocus={(e) => { activeRef.current = { el: e.target, apply: (v) => onBodyChange && onBodyChange(v) }; }} data-testid="review-body" />
              <p className="text-xs text-cass-slate mt-1">A secure "Review your proposal" button/link is added automatically below this message.</p>
              <PlaceholderBar activeRef={activeRef} className="mt-2" />
            </div>
            <label className="flex items-center gap-2 text-xs text-cass-navy cursor-pointer">
              <Checkbox checked={saveDefault} onCheckedChange={(v) => onSaveDefaultChange && onSaveDefaultChange(!!v)} data-testid="review-save-default" />
              Save this subject &amp; message as the new default for future proposals
            </label>
          </div>
        )}
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} data-testid="review-close">{sentLink ? "Done" : "Cancel"}</Button>
          {!sentLink && <Button className="bg-cass-teal text-white" onClick={onSend} disabled={sending} data-testid="review-send-btn"><Send className="w-4 h-4 mr-2" />{sending ? "Sending\u2026" : "Send now"}</Button>}
        </div>
      </Card>
    </div>
  );
}
